/**
 * viewportPersistence.js — Enterprise Session & Annotation Persistence
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — ULTIMATE PERSISTENCE SUITE]:
 *  - replaySession: один рекурсивный setTimeout вместо 1200 таймеров
 *  - _sanitize: глубокая рекурсивная очистка (deep sanitize)
 *  - exportSessionHTML: сжатие скриншотов (JPEG quality 80%)
 *  - _db: закрывается при destroyPersistence
 *  - Аудит-лог действий (getAuditLog, exportAuditLog)
 *  - DICOM SR экспорт (exportDICOMSR)
 *  - Сравнение аннотаций между исследованиями (compareAnnotations)
 *  - Шаблоны отчётов (saveReportTemplate, loadReportTemplate)
 *  - Инкрементальное сохранение сессии (enableAutoSaveSession)
 *  - Восстановление сессии после краша (recoverLastSession)
 *  - Многопользовательские аннотации (setAnnotationAuthor)
 *  - Экспорт в PDF (exportReportPDF)
 *
 * @module viewportPersistence
 */

import { getRenderingEngine, getViewportIds, cornerstone, cornerstoneTools } from './viewportState.js';
import { screenshotViewport } from './viewportScreenshot.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { СТРОКА, дляЭкспорта } from './disclaimer.js';   // [10.19.9] оговорка в каждом экспорте

// ══════════════════════════════════════════════════════════════
//  ABORT CONTROLLER
// ══════════════════════════════════════════════════════════════

let _ac = new AbortController();
function _resetAC() { try { _ac.abort(); } catch {} _ac = new AbortController(); }

// ══════════════════════════════════════════════════════════════
//  AUDIT LOG
// ══════════════════════════════════════════════════════════════

const _auditLog = [];
const MAX_AUDIT_ENTRIES = 10000;

function _addAuditEntry(action, detail = {}) {
  _auditLog.push({
    timestamp: Date.now(),
    isoTime: new Date().toISOString(),
    action,
    detail: _deepSanitize(detail),
    sessionId: _session?.meta?.sessionId || null,
  });
  if (_auditLog.length > MAX_AUDIT_ENTRIES) _auditLog.shift();

  // Инкрементальное сохранение если включено
  if (_autoSaveSession) {
    _dirtySession = true;
  }
}

export function getAuditLog(limit = 100) {
  return _auditLog.slice(-limit);
}

export function exportAuditLog() {
  return JSON.stringify(_auditLog, null, 2);
}

// ══════════════════════════════════════════════════════════════
//  ANNOTATION AUTHOR
// ══════════════════════════════════════════════════════════════

let _annotationAuthor = { name: 'Врач', color: '#4fc3f7', initials: 'ВР' };

export function setAnnotationAuthor(author) {
  _annotationAuthor = { ..._annotationAuthor, ...author };
}

// ══════════════════════════════════════════════════════════════
//  REPORT TEMPLATES
// ══════════════════════════════════════════════════════════════

const _reportTemplates = new Map();

// Дефолтные шаблоны
_reportTemplates.set('ct-chest', {
  name: 'КТ Грудной клетки',
  sections: ['Клинические данные', 'Методика исследования', 'Лёгкие и бронхи', 'Средостение', 'Плевра', 'Костные структуры', 'Заключение'],
});
_reportTemplates.set('ct-abdomen', {
  name: 'КТ ОБП',
  sections: ['Клинические данные', 'Методика', 'Печень', 'Желчный пузырь', 'Поджелудочная', 'Почки', 'Селезёнка', 'Забрюшинное пространство', 'Заключение'],
});
_reportTemplates.set('ct-brain', {
  name: 'КТ Головного мозга',
  sections: ['Клинические данные', 'Методика', 'Кости черепа', 'Ликворные пространства', 'Вещество мозга', 'Срединные структуры', 'Заключение'],
});

export function saveReportTemplate(name, template) {
  _reportTemplates.set(name, template);
}

export function loadReportTemplate(name) {
  return _reportTemplates.get(name) || null;
}

export function listReportTemplates() {
  return [..._reportTemplates.entries()].map(([name, t]) => ({ name, sections: t.sections?.length || 0 }));
}

// ══════════════════════════════════════════════════════════════
//  SESSION RECORDING (с автосохранением)
// ══════════════════════════════════════════════════════════════

let _session = null;
let _autoSaveSession = false;
let _dirtySession = false;
let _autoSaveInterval = null;

export function enableAutoSaveSession(intervalMs = 10000) {
  _autoSaveSession = true;
  if (_autoSaveInterval) clearInterval(_autoSaveInterval);
  _autoSaveInterval = setInterval(() => {
    if (_dirtySession && _session?.active) {
      _saveSessionToStorage();
      _dirtySession = false;
    }
  }, intervalMs);
}

function _saveSessionToStorage() {
  if (!_session) return;
  try {
    const data = {
      meta: _session.meta,
      events: _session.events.slice(-500), // последние 500 событий
      timestamp: Date.now(),
    };
    localStorage.setItem('mikhmel_recovery_session', JSON.stringify(data));
  } catch (e) { log.debug('[Persistence] autoSave:', e.message); }
}

export function recoverLastSession() {
  try {
    const raw = localStorage.getItem('mikhmel_recovery_session');
    if (!raw) return null;
    const data = JSON.parse(raw);
    const age = Date.now() - data.timestamp;
    if (age > 3600000) { // старше 1 часа — не предлагаем
      localStorage.removeItem('mikhmel_recovery_session');
      return null;
    }
    return data;
  } catch { return null; }
}

export function startSessionRecording(meta = {}) {
  if (_session?.active) stopSessionRecording();
  _session = {
    events: [],
    startTime: performance.now(),
    meta: { ...meta, startDate: new Date().toISOString(), sessionId: `session_${Date.now()}` },
    active: true,
    _unsubs: [],
    _snapshotInterval: null,
    screenshots: [],
  };

  _addAuditEntry('SESSION_START', { meta: _session.meta });

  const handler = (evtData, evtName) => {
    if (!_session?.active) return;
    const clean = _deepSanitize(evtData);
    _session.events.push({ t: Math.round(performance.now() - _session.startTime), type: evtName, data: clean });
    _addAuditEntry(evtName, clean);
  };

  const trackEvents = [
    EVENTS.VOLUME_LOAD_DONE, EVENTS.VOLUME_LOAD_START,
    EVENTS.CINE_START, EVENTS.CINE_STOP,
    EVENTS.HU_PROBE, EVENTS.VOICE_COMMAND, EVENTS.ROI_STATS,
    EVENTS.CALCIUM_SCORE_DONE, EVENTS.HANGING_PROTOCOL_APPLIED,
    EVENTS.PATHOLOGY_FOUND, EVENTS.VOLUME_STATS,
  ];

  for (const evtName of trackEvents) {
    if (!evtName) continue;
    const w = (data) => handler(data, evtName);
    events.on(evtName, w);
    _session._unsubs.push(() => events.off(evtName, w));
  }

  // Camera snapshots every 500ms
  // [12.0.57] В снимок вида добавлены номер кадра стопки и окно W/L: листание
  // стопки камеру не двигает (меняется кадр), и прежняя запись «Нативных
  // срезов» при повторе стояла на месте. Одинаковые подряд снимки не пишутся.
  let _прежний = '';
  _session._snapshotInterval = setInterval(() => {
    if (!_session?.active) return;
    const re = getRenderingEngine();
    if (!re) return;
    const snapshot = {};
    for (const vpId of getViewportIds()) {
      try {
        const vp = re.getViewport(vpId);
        const cam = vp?.getCamera();
        if (cam) {
          const voi = vp.getProperties?.()?.voiRange;
          snapshot[vpId] = {
            position: cam.position ? [...cam.position] : null,
            focalPoint: cam.focalPoint ? [...cam.focalPoint] : null,
            viewUp: cam.viewUp ? [...cam.viewUp] : null,
            parallelScale: cam.parallelScale,
            ...(typeof vp.setImageIdIndex === 'function' ? { кадр: vp.getCurrentImageIdIndex?.() ?? null } : {}),
            ...(voi ? { voi: { lower: voi.lower, upper: voi.upper } } : {}),
          };
        }
      } catch (e) { log.debug("[viewportPersistence]", e.message); }
    }
    const ключ = JSON.stringify(snapshot);
    if (ключ === _прежний) return;
    _прежний = ключ;
    _session.events.push({ t: Math.round(performance.now() - _session.startTime), type: 'CAMERA_SNAPSHOT', data: snapshot });
  }, 500);

  log.debug('[Session] Recording started');
  return _session;
}

export function stopSessionRecording() {
  if (!_session) return null;
  _session.active = false;
  clearInterval(_session._snapshotInterval);
  _session._unsubs.forEach(fn => { try { fn(); } catch (e) { log.debug("[viewportPersistence]", e.message); } });

  // Capture final screenshots (сжатые JPEG)
  // [12.0.57] Прежде сюда клались ОБЕЩАНИЯ (_compressScreenshot возвращает
  // Promise), и в выгруженном JSON было "finalScreenshots":{"axial":{}} —
  // снимков не было ни одного. JPEG теперь даёт сам screenshotViewport, сразу.
  const finalScreenshots = {};
  for (const vpId of getViewportIds()) {
    try {
      const dataUrl = screenshotViewport(vpId, { overlay: true, format: 'jpeg', quality: 0.8 });
      if (dataUrl) finalScreenshots[vpId] = dataUrl;
    } catch (e) { log.debug("[viewportPersistence]", e.message); }
  }

  const data = {
    meta: _session.meta,
    duration: Math.round(performance.now() - _session.startTime),
    eventCount: _session.events.length,
    events: _session.events,
    finalScreenshots,
  };

  _addAuditEntry('SESSION_STOP', { duration: data.duration, eventCount: data.eventCount });

  _session = null;

  // Очищаем recovery
  try { localStorage.removeItem('mikhmel_recovery_session'); } catch {}

  log.debug(`[Session] Stopped: ${data.eventCount} events, ${(data.duration / 1000).toFixed(1)}s`);
  return data;
}

// [12.0.57] _compressScreenshot удалена: возвращала Promise, который клался в
// данные записи вместо картинки (см. stopSessionRecording). JPEG даёт screenshotViewport.

// ══════════════════════════════════════════════════════════════
//  REPLAY (один рекурсивный setTimeout)
// ══════════════════════════════════════════════════════════════

export function replaySession(sessionData, opts = {}) {
  if (!sessionData?.events?.length) return null;

  const speed = opts.speed || 1;
  let stopped = false;
  let currentIdx = 0;
  const re = getRenderingEngine();
  let timeoutId = null;

  const playNext = () => {
    if (stopped || currentIdx >= sessionData.events.length) {
      if (!stopped) showToast('⏹ Воспроизведение завершено', 'info', 2000);
      return;
    }

    const evt = sessionData.events[currentIdx];
    currentIdx++;

    // Обработка события
    // [12.0.57] Кадр стопки и окно W/L — тоже (см. запись выше).
    if (evt.type === 'CAMERA_SNAPSHOT' && evt.data && re) {
      for (const [vpId, cam] of Object.entries(evt.data)) {
        try {
          const vp = re.getViewport(vpId);
          if (!vp || !cam) continue;
          const { кадр, voi, ...камера } = cam;
          vp.setCamera(камера);
          if (Number.isInteger(кадр) && typeof vp.setImageIdIndex === 'function') vp.setImageIdIndex(кадр);
          if (voi) vp.setProperties?.({ voiRange: voi });
          vp.render();
        } catch (e) { log.debug("[viewportPersistence]", e.message); }
      }
    }
    opts.onEvent?.(evt);

    // Планируем следующий с учётом времени
    if (currentIdx < sessionData.events.length) {
      const nextEvt = sessionData.events[currentIdx];
      const delay = Math.max(16, Math.round((nextEvt.t - evt.t) / speed));
      timeoutId = setTimeout(playNext, delay);
    } else {
      timeoutId = setTimeout(() => {
        if (!stopped) showToast('⏹ Воспроизведение завершено', 'info', 2000);
      }, Math.round((sessionData.duration - evt.t) / speed) + 500);
    }
  };

  // Запуск с первого события
  const firstDelay = Math.round(sessionData.events[0].t / speed);
  timeoutId = setTimeout(playNext, firstDelay);
  showToast(`▶ Воспроизведение (${speed}x)`, 'info', 2000);

  return { stop() { stopped = true; if (timeoutId) clearTimeout(timeoutId); } };
}

// ══════════════════════════════════════════════════════════════
//  EXPORT
// ══════════════════════════════════════════════════════════════

export function exportSessionJSON(sessionData) {
  return JSON.stringify({ version: 2, type: 'mikhmel-session-recording', ...sessionData });
}

export function exportSessionHTML(sessionData) {
  const { meta, duration, eventCount, finalScreenshots } = sessionData || {};
  const durMin = ((duration || 0) / 60000).toFixed(1);

  const screenshotsHTML = Object.entries(finalScreenshots || {})
    .map(([vpId, dataUrl]) => dataUrl ? `<div style="margin:8px;display:inline-block;"><img src="${dataUrl}" style="max-width:400px;border:1px solid #334155;border-radius:4px;" loading="lazy"/><div style="text-align:center;color:#94a3b8;font-size:12px;">${vpId}</div></div>` : '')
    .join('');

  const significantEvents = (sessionData?.events || []).filter(e => e.type !== 'CAMERA_SNAPSHOT').slice(-30);
  const eventRows = significantEvents.map(e => `<tr><td style="color:#60a5fa;">${(e.t / 1000).toFixed(1)}s</td><td>${e.type}</td></tr>`).join('');

  return `<!DOCTYPE html>
<html lang="ru"><head><meta charset="utf-8"><title>Session Report — ${meta?.startDate || ''}</title>
<style>body{background:#0f172a;color:#e2e8f0;font-family:system-ui;padding:40px;max-width:900px;margin:auto;}h1{color:#60a5fa;}table{border-collapse:collapse;width:100%;}td,th{padding:6px 12px;border-bottom:1px solid #1e293b;text-align:left;font-size:13px;}</style></head>
<body>
<h1>📋 Отчёт сессии просмотра</h1>
<p><strong>Дата:</strong> ${meta?.startDate || 'N/A'}<br/><strong>Длительность:</strong> ${durMin} мин<br/><strong>Событий:</strong> ${eventCount || 0}<br/>${meta?.doctorName ? `<strong>Врач:</strong> ${meta.doctorName}<br/>` : ''}${meta?.studyUID ? `<strong>Study UID:</strong> ${meta.studyUID}` : ''}</p>
<h2>Финальные виды</h2>${screenshotsHTML || '<p style="color:#64748b;">Нет скриншотов</p>'}
<h2>Последние события</h2><table><tr><th>Время</th><th>Событие</th></tr>${eventRows || '<tr><td colspan="2" style="color:#64748b;">Нет событий</td></tr>'}</table>
<p style="margin-top:40px;color:#475569;font-size:11px;">Сгенерировано Mikhmel Cornerstone4D DICOM Viewer Pro v6</p>
</body></html>`;
}

// ══════════════════════════════════════════════════════════════
//  DICOM SR EXPORT
// ══════════════════════════════════════════════════════════════

/**
 * Экспорт измерений в DICOM Structured Report (упрощённый).
 * @param {Object[]} measurements — массив { type, value, unit, coords, label }
 * @param {Object} meta — { studyUID, seriesUID, patientName }
 * @returns {string} DICOM SR JSON
 */
export function exportDICOMSR(measurements, meta = {}) {
  const content = measurements.map((m, i) => ({
    ConceptNameCodeSequence: { CodeValue: m.type || 'MEASUREMENT', CodingSchemeDesignator: '99MIKHMEL', CodeMeaning: m.label || m.type },
    MeasuredValueSequence: { NumericValue: m.value, MeasurementUnitsCodeSequence: { CodeValue: m.unit || 'mm', CodingSchemeDesignator: 'UCUM', CodeMeaning: m.unit || 'mm' } },
    ContentSequence: m.coords ? { GraphicData: m.coords, GraphicType: 'POLYLINE' } : undefined,
  }));

  return JSON.stringify({
    Modality: 'SR',
    SOPClassUID: '1.2.840.10008.5.1.4.1.1.88.33',
    StudyInstanceUID: meta.studyUID || '',
    SeriesInstanceUID: meta.seriesUID || '',
    PatientName: meta.patientName || '',
    ContentTemplateSequence: {},
    ContentSequence: content,
    _exportDate: new Date().toISOString(),
    _viewer: 'Mikhmel Cornerstone4D v6',
  }, null, 2);
}

// ══════════════════════════════════════════════════════════════
//  PDF EXPORT
// ══════════════════════════════════════════════════════════════

/**
 * Экспорт в PDF (требует jsPDF). Если библиотека не загружена — возвращает HTML.
 * @param {Object} report — { title, sections: [{ heading, text, measurements }], screenshots }
 * @returns {Promise<Blob|string>}
 */
export async function exportReportPDF(report) {
  // [11.1.19] jsPDF снят и здесь — второй из двух очагов абракадабры: его
  // встроенные шрифты без кириллицы, и «Отчёт», «Дата», «Пациент» и весь
  // текст секций уходили мусором. Бланк собирается HTML'ом (кириллица —
  // родная) и уходит тем же единственным PDF-движком программы —
  // exportModule.открытьПечатное (printToPDF в настольной сборке / диалог
  // печати в вебе). Прежний договор «Blob или HTML-строка» упрощён до
  // строки: единственный известный потребитель — сама печать; строка
  // возвращается для совместимости и проверок.
  const экр = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const секции = (report?.sections || []).map((s) => `
    <h2>${экр(s.heading)}</h2>
    ${s.text ? `<div class="txt">${экр(s.text)}</div>` : ''}
    ${s.measurements?.length ? `<ul>${s.measurements.map((m) => `<li>${экр(m.label || m.type)}: ${экр(m.value)} ${экр(m.unit || 'mm')}</li>`).join('')}</ul>` : ''}
  `).join('');
  const оговорка = (() => { try { return дляЭкспорта(typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''); } catch (e) { return ''; } })();
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${экр(report?.title || 'Отчёт')}</title>
<style>@page{size:A4;margin:15mm}body{font-family:'Times New Roman',serif;color:#111;font-size:11pt;line-height:1.5}
h1{font-size:15pt;margin:0 0 2mm}h2{font-size:12pt;border-bottom:1px solid #999;margin:6mm 0 2mm}
.meta{color:#555;font-size:9pt;margin-bottom:4mm}.txt{white-space:pre-wrap}
.disc{position:fixed;bottom:0;left:0;right:0;border-top:1px solid #ccc;padding-top:1mm;font-size:8pt;color:#777}body{margin-bottom:12mm}</style></head><body>
<h1>${экр(report?.title || 'Отчёт')}</h1>
<div class="meta">Дата: ${экр(new Date().toLocaleString('ru-RU'))}${report?.patientName ? ` · Пациент: ${экр(report.patientName)}` : ''}${report?.studyUID ? ` · Study UID: ${экр(report.studyUID)}` : ''}</div>
${секции}
${оговорка ? `<div class="disc">${экр(оговорка)}</div>` : ''}
</body></html>`;
  try {
    const { открытьПечатное } = await import('../ui/exportModule.js');
    await открытьПечатное(html, 'width=820,height=900');
  } catch (e) { /* печать недоступна — строку всё равно вернём */ }
  return html;
}

// [11.1.19] Хвост между функциями восстановлен: срез при переписывании
// exportReportPDF случайно унёс приватные объявления (_deepSanitize,
// _studyTimer и соседей), а их вызовы ниже остались — mirrors-сторож поймал.


// ══════════════════════════════════════════════════════════════
//  SANITIZE (глубокая рекурсивная)
// ══════════════════════════════════════════════════════════════

function _deepSanitize(data, depth = 0) {
  if (depth > 10) return '[MaxDepth]';
  if (data === null || data === undefined) return data;
  if (typeof data === 'function') return '[Function]';
  if (data instanceof HTMLElement) return `<${data.tagName}#${data.id}>`;
  if (ArrayBuffer.isView(data)) return `[TypedArray:${data.length}]`;
  if (data instanceof Promise) return '[Promise]';
  if (typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    return data.map(v => _deepSanitize(v, depth + 1));
  }

  const clean = {};
  for (const [k, v] of Object.entries(data)) {
    if (k.startsWith('_')) continue; // skip private
    try {
      clean[k] = _deepSanitize(v, depth + 1);
    } catch { clean[k] = '[Unserializable]'; }
  }
  return clean;
}

function _sanitize(data) {
  return _deepSanitize(data);
}

// ══════════════════════════════════════════════════════════════
//  STUDY SESSION TIMER
// ══════════════════════════════════════════════════════════════

let _studyTimer = null;

export function startStudyTimer(studyUID) {
  stopStudyTimer();
  _studyTimer = { studyUID, startTime: Date.now(), pausedAt: null, totalPaused: 0 };
  _addAuditEntry('STUDY_TIMER_START', { studyUID });
}

export function pauseStudyTimer() {
  if (_studyTimer && !_studyTimer.pausedAt) _studyTimer.pausedAt = Date.now();
}

export function resumeStudyTimer() {
  if (_studyTimer?.pausedAt) {
    _studyTimer.totalPaused += Date.now() - _studyTimer.pausedAt;
    _studyTimer.pausedAt = null;
  }
}

export function stopStudyTimer() {
  if (!_studyTimer) return null;
  const elapsed = Date.now() - _studyTimer.startTime - _studyTimer.totalPaused;
  const result = { studyUID: _studyTimer.studyUID, elapsedMs: elapsed, elapsedMin: +(elapsed / 60000).toFixed(1) };
  _addAuditEntry('STUDY_TIMER_STOP', result);
  _studyTimer = null;
  return result;
}

export function getStudyTimerElapsed() {
  if (!_studyTimer) return 0;
  const paused = _studyTimer.pausedAt ? (Date.now() - _studyTimer.pausedAt) : 0;
  return Date.now() - _studyTimer.startTime - _studyTimer.totalPaused - paused;
}

// ══════════════════════════════════════════════════════════════
//  ANNOTATION PERSISTENCE (IndexedDB)
// ══════════════════════════════════════════════════════════════

const _DB_NAME = 'mikhmelAnnotations', _DB_VER = 1, _STORE = 'annotations';
let _db = null;

async function _openDB() {
  if (_db) return _db;
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(_DB_NAME, _DB_VER);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(_STORE)) {
        const s = db.createObjectStore(_STORE, { keyPath: 'id' });
        s.createIndex('studyUID', 'studyUID', { unique: false });
        s.createIndex('seriesUID', 'seriesUID', { unique: false });
        s.createIndex('author', 'author', { unique: false });
      }
    };
    req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
    req.onerror = (e) => reject(e);
  });
}

export async function saveAnnotation(annotation) {
  try {
    const db = await _openDB();
    const tx = db.transaction(_STORE, 'readwrite');
    tx.objectStore(_STORE).put({
      ...annotation,
      timestamp: annotation.timestamp || Date.now(),
      version: 1,
      author: annotation.author || _annotationAuthor.name,
      authorColor: annotation.authorColor || _annotationAuthor.color,
    });
    await new Promise((r, j) => { tx.oncomplete = r; tx.onerror = j; });
    _addAuditEntry('ANNOTATION_SAVE', { id: annotation.id, type: annotation.type });
  } catch (e) { log.warn('[Annotations] Save:', e.message); }
}

export async function loadAnnotations(studyUID) {
  try {
    const db = await _openDB();
    return new Promise((r, j) => {
      const req = db.transaction(_STORE, 'readonly').objectStore(_STORE).index('studyUID').getAll(studyUID);
      req.onsuccess = () => r(req.result || []);
      req.onerror = j;
    });
  } catch { return []; }
}

/**
 * Сравнить аннотации между двумя исследованиями.
 * @param {string} studyUID1
 * @param {string} studyUID2
 * @returns {Promise<Object>} { added, removed, changed, unchanged }
 */
export async function compareAnnotations(studyUID1, studyUID2) {
  const [anns1, anns2] = await Promise.all([loadAnnotations(studyUID1), loadAnnotations(studyUID2)]);

  const map1 = new Map(anns1.map(a => [a.id, a]));
  const map2 = new Map(anns2.map(a => [a.id, a]));

  const added = anns2.filter(a => !map1.has(a.id));
  const removed = anns1.filter(a => !map2.has(a.id));
  const changed = [];
  const unchanged = [];

  for (const a1 of anns1) {
    const a2 = map2.get(a1.id);
    if (!a2) continue;
    if (JSON.stringify(a1.data) !== JSON.stringify(a2.data)) {
      changed.push({ id: a1.id, before: a1, after: a2 });
    } else {
      unchanged.push(a1);
    }
  }

  return { added, removed, changed, unchanged, total1: anns1.length, total2: anns2.length };
}

export async function deleteAnnotation(annotationId) {
  try {
    const db = await _openDB();
    const tx = db.transaction(_STORE, 'readwrite');
    tx.objectStore(_STORE).delete(annotationId);
    await new Promise((r, j) => { tx.oncomplete = r; tx.onerror = j; });
    _addAuditEntry('ANNOTATION_DELETE', { id: annotationId });
  } catch (e) { log.warn('[Persistence] deleteAnnotation failed:', e.message); }
}

export async function clearAnnotations(studyUID) {
  try {
    const anns = await loadAnnotations(studyUID);
    const db = await _openDB();
    const tx = db.transaction(_STORE, 'readwrite');
    for (const a of anns) tx.objectStore(_STORE).delete(a.id);
    await new Promise((r, j) => { tx.oncomplete = r; tx.onerror = j; });
    _addAuditEntry('ANNOTATION_CLEAR', { studyUID, count: anns.length });
  } catch (e) { log.warn(`[viewportPersistence] clearAnnotations failed:`, e.message); }
}

export async function exportAnnotationsJSON(studyUID) {
  try {
    return JSON.stringify({
      version: 1, studyUID, exportDate: new Date().toISOString(),
      annotations: await loadAnnotations(studyUID),
    }, null, 2);
  } catch (e) { log.warn(`[viewportPersistence] exportAnnotationsJSON failed:`, e.message); return null; }
}

export async function importAnnotationsJSON(jsonStr) {
  try {
    const data = JSON.parse(jsonStr);
    if (!data.annotations?.length) return 0;
    let count = 0;
    for (const a of data.annotations) {
      if (a.id && a.studyUID && a.type) { await saveAnnotation(a); count++; }
    }
    _addAuditEntry('ANNOTATION_IMPORT', { count });
    return count;
  } catch (e) { log.warn(`[viewportPersistence] importAnnotationsJSON failed:`, e.message); return 0; }
}

export async function getAnnotationStats(studyUID) {
  try {
    const anns = await loadAnnotations(studyUID);
    const byType = {};
    const byAuthor = {};
    for (const a of anns) {
      byType[a.type] = (byType[a.type] || 0) + 1;
      byAuthor[a.author || 'unknown'] = (byAuthor[a.author || 'unknown'] || 0) + 1;
    }
    return {
      total: anns.length, byType, byAuthor,
      oldestTimestamp: anns.length ? Math.min(...anns.map(a => a.timestamp)) : null,
      newestTimestamp: anns.length ? Math.max(...anns.map(a => a.timestamp)) : null,
    };
  } catch (e) { log.warn(`[viewportPersistence] getAnnotationStats failed:`, e.message); return {}; }
}

let _autoSaveTimer = null;

export function enableAnnotationAutoSave(studyUID) {
  if (!studyUID) return;
  const EVT = cornerstoneTools?.Enums?.Events?.ANNOTATION_MODIFIED || 'CORNERSTONE_TOOLS_ANNOTATION_MODIFIED';
  try {
    cornerstone.eventTarget.addEventListener(EVT, () => {
      clearTimeout(_autoSaveTimer);
      _autoSaveTimer = setTimeout(async () => {
        try {
          const allAnns = cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || [];
          for (const ann of allAnns) {
            if (!ann.annotationUID) continue;
            await saveAnnotation({
              id: ann.annotationUID,
              studyUID,
              seriesUID: ann.metadata?.seriesInstanceUID || '',
              type: ann.metadata?.toolName || 'unknown',
              data: ann.data,
              metadata: ann.metadata,
            });
          }
        } catch (e) { log.warn('[Persistence] autoSave failed:', e.message); }
      }, 2000);
    }, { signal: _ac.signal });
  } catch (e) { log.warn('[Persistence] enableAnnotationAutoSave bind failed:', e.message); }
}

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyPersistence() {
  _resetAC();
  if (_autoSaveInterval) clearInterval(_autoSaveInterval);
  if (_session?.active) stopSessionRecording();
  if (_db) {
    try { _db.close(); } catch {}
    _db = null;
  }
  _auditLog.length = 0;
  _reportTemplates.clear();
}